import { tokens } from "../../../../theme";
import { Box, Typography, useTheme } from "@mui/material";
import { WarningAmberOutlined } from "@mui/icons-material";
import { refreshDrawerStyles } from "./styles";
import {
  GROUP_LABELS,
  MODES,
  isCompleteExtra,
  isOverridden,
  plural,
  scopeSummary,
  usesDefaultBranch,
} from "./helpers";
import { NEXUS_REPO, TENANT_UI } from "../../../../utils/constants";

const Row = ({ label, children }) => {
  const theme = useTheme();
  const { refreshDrawer } = refreshDrawerStyles(tokens(theme.paletteId));
  return (
    <Box sx={refreshDrawer.reviewRow}>
      <Typography sx={refreshDrawer.reviewLabel}>{label}</Typography>
      <Box sx={refreshDrawer.reviewValue}>{children}</Box>
    </Box>
  );
};

// One line per override: key · registry/name · tag. Added rows are flagged so
// they read differently from edits to manifest rows.
const overrideLines = (groups, edits, extras) =>
  groups.flatMap(({ group, rows }) => [
    ...rows
      .filter((row) => isOverridden(row, edits?.[group]?.[row.key]))
      .map((row) => ({
        group,
        key: row.key,
        added: false,
        ...edits[group][row.key],
      })),
    ...(extras?.[group] ?? [])
      .filter(isCompleteExtra)
      .map((extra) => ({ group, added: true, ...extra })),
  ]);

const ReviewPanel = ({
  env,
  mode,
  releaseLabel,
  scope,
  groups,
  edits,
  extras,
  uiTag,
  baseConfigBranch,
  inScopeCount,
  totalCount,
}) => {
  const theme = useTheme();
  const { refreshDrawer } = refreshDrawerStyles(tokens(theme.paletteId));

  const lines =
    mode === MODES.CUSTOM ? overrideLines(groups, edits, extras) : [];
  const unchanged = Math.max(
    totalCount - lines.filter((l) => !l.added).length,
    0
  );

  return (
    <>
      <Box sx={refreshDrawer.card}>
        <Box sx={refreshDrawer.cardHead}>
          <Typography sx={refreshDrawer.cardTitle}>Summary</Typography>
        </Box>
        <Box sx={{ ...refreshDrawer.cardBody, gap: 0 }}>
          <Row label="Environment">
            <Typography sx={refreshDrawer.reviewMono}>
              {env?.name || "-"}
            </Typography>
          </Row>
          {mode === MODES.UI ? (
            <>
              <Row label="Mode">UI only</Row>
              <Row label="Tag">
                <Typography sx={refreshDrawer.reviewMono}>
                  {uiTag.trim()}
                </Typography>
              </Row>
              <Row label="Artifact">
                <Typography sx={refreshDrawer.reviewMono}>
                  {`${TENANT_UI.rmoKey} \u00b7 ${NEXUS_REPO.devPullRepo}`}
                </Typography>
              </Row>
            </>
          ) : (
            <>
              <Row label="Mode">
                {mode === MODES.CUSTOM ? "Custom refresh" : "Standard refresh"}
              </Row>
              <Row label="Target">
                <Typography sx={refreshDrawer.reviewMono}>
                  {releaseLabel}
                </Typography>
              </Row>
              <Row label="Scope">{scopeSummary(scope)}</Row>
              <Row label="Base config branch">
                <Typography sx={refreshDrawer.reviewMono}>
                  {usesDefaultBranch(scope)
                    ? "release default"
                    : baseConfigBranch.trim()}
                </Typography>
              </Row>
              {mode === MODES.CUSTOM ? (
                <Row label="In scope">{`${plural(
                  inScopeCount,
                  "item"
                )} of ${totalCount}`}</Row>
              ) : null}
            </>
          )}
        </Box>
      </Box>

      {mode === MODES.CUSTOM ? (
        <>
          <Box sx={refreshDrawer.card}>
            <Box sx={refreshDrawer.cardHead}>
              <Typography sx={refreshDrawer.cardTitle}>Overrides</Typography>
              <Typography sx={refreshDrawer.cardCount}>
                {plural(lines.length, "override")}
              </Typography>
            </Box>
            <Box sx={{ ...refreshDrawer.cardBody, gap: 0 }}>
              {lines.length ? (
                lines.map(({ group, key, name, tag, registry, added }) => (
                  <Box key={`${group}-${key}`} sx={refreshDrawer.overrideLine}>
                    <Typography sx={refreshDrawer.overrideKey} title={key}>
                      {added ? `+ ${key}` : key}
                    </Typography>
                    <Typography
                      sx={refreshDrawer.overrideName}
                      title={`${registry}/${name}`}
                    >
                      {`${
                        GROUP_LABELS[group] ?? group
                      } \u00b7 ${registry}/${name}`}
                    </Typography>
                    <Typography sx={refreshDrawer.overrideTag}>
                      {tag}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={refreshDrawer.emptyRow}>
                  No overrides — the manifest defaults apply.
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={refreshDrawer.card}>
            <Box sx={refreshDrawer.cardHead}>
              <Typography sx={refreshDrawer.cardTitle}>Unchanged</Typography>
              <Typography sx={refreshDrawer.cardCount}>
                {plural(unchanged, "item")}
              </Typography>
            </Box>
            <Box sx={refreshDrawer.cardBody}>
              <Box sx={refreshDrawer.pillRow}>
                {groups.map(({ group, label, rows }) => {
                  const kept = rows.filter(
                    (row) => !isOverridden(row, edits?.[group]?.[row.key])
                  ).length;
                  return (
                    <Box key={group} sx={refreshDrawer.neutralPill}>
                      {`${label} \u00b7 ${kept}`}
                    </Box>
                  );
                })}
              </Box>
              <Typography sx={refreshDrawer.cardDescription}>
                These move to the {releaseLabel} manifest values.
              </Typography>
            </Box>
          </Box>
        </>
      ) : null}

      <Box sx={refreshDrawer.reviewWarn}>
        <WarningAmberOutlined sx={{ fontSize: 18 }} />
        <Typography component="span" sx={refreshDrawer.reviewWarnText}>
          The environment restarts during the refresh and is unavailable until
          it finishes.
        </Typography>
      </Box>
    </>
  );
};

export default ReviewPanel;
